import { TimePicker } from "@totalenergiescode/mobility-business-rev-design-system";
import {
  Suffix,
  translateField,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";
import dayjs from "dayjs";

import { StyledTimePicker } from "../TimePickerField/styles";

interface TimeRangePickerFieldProps {
  fieldId: string;
}

function TimeRangePickerField({ fieldId }: TimeRangePickerFieldProps) {
  const {
    dataTestIdPrefix,
    findFieldById,
    findCompositeFieldByName,
    translatorFor,
  } = useFormDataContext();

  const startField = findFieldById(fieldId);
  const endField = findCompositeFieldByName(startField, "endHour");

  const startTranslator = translatorFor(startField);
  const startLabel = translateField(startTranslator, startField, Suffix.LABEL);

  const endTranslator = translatorFor(endField);
  const endLabel = translateField(endTranslator, endField, Suffix.LABEL);

  const startValue = startField.value.asString();
  const currentStart = startValue.value() || startValue.defaultValue();
  const startTimeValue = currentStart
    ? dayjs(currentStart, "HH:mm")
    : undefined;

  const endValue = endField.value.asString();
  const currentEnd = endValue.value() || endValue.defaultValue();
  const endTimeValue = currentEnd ? dayjs(currentEnd, "HH:mm") : undefined;

  function isNewStartAfterCurrentEnd(newValue: dayjs.Dayjs | null) {
    return newValue?.isAfter(endTimeValue);
  }

  function isNewEndBeforeCurrentStart(newValue: dayjs.Dayjs | null) {
    return newValue?.isBefore(startTimeValue);
  }

  function handleStartTimeChange(newValue: dayjs.Dayjs | null) {
    const newTime = newValue?.format("HH:mm") || "";
    startField.setValue(newTime);

    if (isNewStartAfterCurrentEnd(newValue)) {
      endField.setValue("");
    }
  }

  function handleEndTimeChange(newValue: dayjs.Dayjs | null) {
    const newTime = newValue?.format("HH:mm") || "";
    endField.setValue(newTime);

    if (isNewEndBeforeCurrentStart(newValue)) {
      startField.setValue("");
    }
  }

  return (
    <div>
      <StyledTimePicker>
        <TimePicker
          dataTestId={`${dataTestIdPrefix}-${startField.id()}`}
          label={startLabel.toString()}
          isDisabled={!startField.writable}
          maxTime={endTimeValue}
          onChange={handleStartTimeChange}
          value={startTimeValue}
        />
      </StyledTimePicker>
      <StyledTimePicker>
        <TimePicker
          dataTestId={`${dataTestIdPrefix}-${endField.id()}`}
          label={endLabel.toString()}
          isDisabled={!endField.writable}
          minTime={startTimeValue}
          onChange={handleEndTimeChange}
          value={endTimeValue}
        />
      </StyledTimePicker>
    </div>
  );
}

export default TimeRangePickerField;
